/**
 * TrendPanel - Tendência Mercado HeveaTalent
 * Veredito → Faixa de Negociação (2 referências + CONAB + justo + teto)
 * → Tendência com gráfico 60 dias → Entenda o cálculo (expansível)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { TrendChart } from './TrendChart';
import { EntendaCalculo } from './EntendaCalculo';

const fmtBRL = (v) => `R$ ${Number(v).toFixed(2).replace('.', ',')}`;
const fmtPct = (v) => `${v > 0 ? '+' : ''}${String(v).replace('.', ',')}%`;

const VERDICT_STYLE = {
  unfavorable: { bg: 'bg-red-600', sub: 'text-red-100', icon: '🔴' },
  warning: { bg: 'bg-amber-500', sub: 'text-amber-50', icon: '🟡' },
  favorable: { bg: 'bg-green-600', sub: 'text-green-100', icon: '🟢' },
};

const DIRECTION = {
  alta: { label: 'TENDÊNCIA DE ALTA', icon: '↗️', color: 'text-green-700', chip: 'bg-green-100' },
  queda: { label: 'TENDÊNCIA DE QUEDA', icon: '↘️', color: 'text-red-600', chip: 'bg-red-100' },
  estavel: { label: 'TENDÊNCIA ESTÁVEL', icon: '➡️', color: 'text-gray-700', chip: 'bg-gray-100' },
};

export default function TrendPanel() {
  const [data, setData] = useState(null);
  const [backtest, setBacktest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trendRes, btRes] = await Promise.all([
        fetch(`${API_URL}/api/market/trend`),
        fetch(`${API_URL}/api/market/trend/backtest`),
      ]);
      if (!trendRes.ok) throw new Error('trend');
      setData(await trendRes.json());
      if (btRes.ok) setBacktest(await btRes.json());
    } catch (e) {
      setError('Não foi possível carregar a tendência do mercado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="trend-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-xl p-6 text-center" data-testid="trend-error">
        <p className="text-red-800 font-medium">⚠️ {error}</p>
        <button onClick={load} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg">Tentar novamente</button>
      </div>
    );
  }

  const { verdict, faixa, trend, confidence, impact } = data;
  const vs = VERDICT_STYLE[verdict.level] || VERDICT_STYLE.warning;
  const dir = DIRECTION[trend.direction] || DIRECTION.estavel;

  // posição do marcador do mercado na régua
  const lo = Math.min(faixa.market?.value || faixa.conab_min.value, faixa.conab_min.value) * 0.93;
  const hi = faixa.teto * 1.04;
  const markerPct = faixa.market?.value
    ? Math.min(96, Math.max(2, ((faixa.market.value - lo) / (hi - lo)) * 100))
    : null;

  const marketRefLabel = faixa.market?.reference_date
    ? faixa.market.reference_date.length === 7
      ? `${['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'][parseInt(faixa.market.reference_date.slice(5,7),10)-1]}/${faixa.market.reference_date.slice(2,4)}`
      : faixa.market.reference_date
    : '';

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="trend-panel">
      {/* 1. VEREDITO */}
      <div className={`${vs.bg} rounded-2xl shadow p-3 text-center`} data-testid="trend-verdict-banner">
        <p className="text-white font-black text-sm">{vs.icon} {verdict.title}</p>
        <p className={`${vs.sub} text-[11px] mt-0.5`}>{verdict.subtitle} {verdict.asymmetry}</p>
      </div>

      {/* 2. FAIXA DE NEGOCIAÇÃO */}
      <div className="bg-white rounded-2xl shadow p-4 border-t-4 border-green-700" data-testid="trend-faixa-card">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-green-900">📊 Faixa de Negociação do Coágulo</h2>
          <span className="text-[10px] bg-green-700 text-white px-2 py-0.5 rounded-full font-semibold">DRC {faixa.drc_pct}%</span>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3 text-center">
          {faixa.reference_previous && (
            <div className="bg-gray-50 rounded-xl p-2">
              <p className="text-[10px] text-gray-500">Referência CNA/IEA · <strong>{faixa.reference_previous.label}</strong></p>
              <p className="text-base font-black text-gray-700">{fmtBRL(faixa.reference_previous.value)}</p>
            </div>
          )}
          <div className="bg-green-50 rounded-xl p-2 border border-green-200">
            <p className="text-[10px] text-green-700">Referência CNA/IEA · <strong>{faixa.reference_current.label}</strong></p>
            <p className="text-base font-black text-green-800">
              {fmtBRL(faixa.reference_current.value)}{' '}
              {faixa.reference_current.variation_pct != null && (
                <span className={`text-[10px] font-bold ${faixa.reference_current.variation_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {faixa.reference_current.variation_pct >= 0 ? '▲' : '▼'} {fmtPct(faixa.reference_current.variation_pct)}
                </span>
              )}
            </p>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mt-1 text-center">Referência de importação (R$/kg seco) — o número que baliza toda a cadeia</p>

        {/* régua */}
        <div className="mt-4 relative h-3 rounded-full bg-gradient-to-r from-red-400 via-amber-300 to-green-500">
          {markerPct != null && (
            <div className="absolute -top-1 w-1 h-5 bg-red-600 rounded" style={{ left: `${markerPct}%` }}></div>
          )}
        </div>
        <div className="grid grid-cols-4 gap-1 mt-2 text-center">
          <div>
            <p className="text-[10px] font-bold text-red-600">MERCADO (IEA{marketRefLabel ? `, ${marketRefLabel}` : ''})</p>
            <p className="text-sm font-black text-red-600" data-testid="trend-market-value">{faixa.market?.value ? fmtBRL(faixa.market.value) : '—'}</p>
            <p className="text-[9px] text-red-400 -mt-0.5">último pago conhecido</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-500">Mínimo CONAB</p>
            <p className="text-sm font-bold text-gray-700">{fmtBRL(faixa.conab_min.value)}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-500">Piso CNA/IEA ({faixa.justo_pct}%)</p>
            <p className="text-sm font-bold text-green-700">{fmtBRL(faixa.justo)}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-500">Teto CNA/IEA ({String(faixa.teto_pct).replace('.', ',')}%)</p>
            <p className="text-sm font-bold text-green-800">{fmtBRL(faixa.teto)}</p>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mt-2">
          Faixa calculada sobre a referência de {faixa.reference_current.label} ({fmtBRL(faixa.reference_current.value)} × DRC {faixa.drc_pct}% × participação histórica)
        </p>
      </div>

      {/* 3. TENDÊNCIA */}
      <div className="bg-white rounded-2xl shadow p-4 border-t-4 border-indigo-600" data-testid="trend-tendencia-card">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-indigo-900">📈 Tendência Mercado HeveaTalent</h2>
          <span className="text-[10px] bg-indigo-600 text-white px-2 py-0.5 rounded-full font-semibold">ATUALIZADO DIARIAMENTE</span>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <div className={`${dir.chip} rounded-2xl w-14 h-14 flex items-center justify-center shrink-0`}>
            <span className="text-3xl">{dir.icon}</span>
          </div>
          <div>
            <p className={`text-lg font-black leading-tight ${dir.color}`} data-testid="trend-direction-label">{dir.label}</p>
            {trend.projected_ref && (
              <p className="text-[11px] text-gray-600">
                Projeção da referência de <strong>{trend.projection_month}</strong>:{' '}
                <strong>{fmtBRL(trend.projection_range[0])} – {fmtBRL(trend.projection_range[1])}</strong>{' '}
                <span className={`font-bold ${trend.variation_vs_current_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ({fmtPct(trend.variation_vs_current_pct)} vs {faixa.reference_current.label})
                </span>
              </p>
            )}
          </div>
        </div>

        {data.why_note?.text && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3" data-testid="trend-why-note">
            <p className="text-[11px] text-amber-900">
              <strong>📝 Por que está assim:</strong> {data.why_note.text}
            </p>
          </div>
        )}

        <div className="mt-3">
          <TrendChart series={trend.series} officialLines={trend.official_lines} />
        </div>

        {impact?.message && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mt-2" data-testid="trend-impact-message">
            <p className="text-[11px] text-indigo-900"><strong>O que isso significa pro seu coágulo:</strong> {impact.message} {verdict.asymmetry}</p>
          </div>
        )}

        {confidence?.months_tested && (
          <p className="text-[10px] text-gray-400 mt-2 text-center">
            🎯 Modelo validado com dados reais: direção correta em {confidence.direction_hits} de {confidence.months_tested} meses ({confidence.hit_rate_pct}%) · erro médio ±{String(confidence.mape_pct).replace('.', ',')}% — detalhes em "Entenda o cálculo"
          </p>
        )}
      </div>

      {/* 4. ENTENDA O CÁLCULO */}
      <EntendaCalculo data={data} backtest={backtest} />
    </div>
  );
}
